import React, { Fragment } from 'react';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    PaginationState,
    Row,
    useReactTable,
} from '@tanstack/react-table';
import { observer } from 'src/utils/mobx-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { cn } from 'src/domains/common/components/utils';
import { DataTableState } from './data-table.state';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { Select } from 'src/domains/common/components/select/select';

interface DataTablePropsType<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    totalCount: number | undefined;
    renderSubComponent: (props: { row: Row<TData> }) => React.ReactElement;
    getRowCanExpand: (row: Row<TData>) => boolean;
    onPaginationChange: (pagination: PaginationState) => void;
}

export const DataTable = observer(
    'DataTable',
    <TData, TValue>({
        columns,
        data,
        totalCount,
        renderSubComponent,
        getRowCanExpand,
        onPaginationChange,
    }: DataTablePropsType<TData, TValue>) => {
        const [state] = React.useState(() => new DataTableState());

        const table = useReactTable({
            data,
            columns,
            getRowCanExpand,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            getExpandedRowModel: getExpandedRowModel(),
            onPaginationChange: (updater) => {
                if (typeof updater === 'function') {
                    const newPageState = updater({ pageIndex: state.pageIndex, pageSize: state.pageSize });
                    state.onPaginationChange(newPageState);
                    onPaginationChange(newPageState);
                }
            },
            manualPagination: true,
            state: {
                pagination: {
                    pageIndex: state.pageIndex,
                    pageSize: state.pageSize,
                },
            },
            pageCount: totalCount === undefined ? -1 : Math.ceil(totalCount / state.pageSize),
        });

        const { pageIndex, pageSize } = table.getState().pagination;
        const current = pageSize * pageIndex;
        const to = current + pageSize;

        const { pageSizeState } = state;

        return (
            <div className='flex flex-col flex-nowrap overflow-hidden'>
                <div className='relative w-full overflow-auto border border-solid rounded-md border-border'>
                    <Table className='table-fixed min-w-[1660px]'>
                        <TableHeader className='h-9 sticky top-0 z-[1] bg-white shadow-b-shadow'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow
                                    key={headerGroup.id}
                                    className='!border-b-0'
                                >
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead
                                                key={header.id}
                                                style={{
                                                    width: header.getSize(),
                                                    minWidth: header.column.columnDef.minSize,
                                                    maxWidth: header.column.columnDef.maxSize,
                                                }}
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row, index) => (
                                    <Fragment key={row.id}>
                                        <TableRow
                                            data-state={row.getIsSelected() && 'selected'}
                                            className={cn('border-border border border-x-0 border-solid', {
                                                'bg-muted/50': index % 2 === 0,
                                                'border-b-0': row.getIsExpanded(),
                                            })}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell
                                                    key={cell.id}
                                                    style={{
                                                        width: cell.column.getSize(),
                                                        minWidth: cell.column.columnDef.minSize,
                                                        maxWidth: cell.column.columnDef.maxSize,
                                                    }}
                                                >
                                                    <div className='flex items-center h-6'>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </div>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        {row.getIsExpanded() && (
                                            <tr
                                                className={cn({
                                                    'bg-muted/50': index % 2 === 0,
                                                })}
                                            >
                                                <td colSpan={row.getVisibleCells().length}>
                                                    {renderSubComponent({ row })}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className='h-24 text-center'
                                    >
                                        No results.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <div className='flex items-center justify-between pt-4 font-tertiary'>
                    <div className='flex items-center gap-2'>
                        <span className='text-xs whitespace-nowrap'>
                            <b className='font-semibold'>
                                {current + 1}-{to < (totalCount ?? -1) ? to : totalCount}{' '}
                            </b>
                            of <b className='font-semibold'>{totalCount}</b>
                            <span className='mx-2'>|</span>Results per page:
                        </span>

                        <Select
                            className='w-28'
                            formInputState={pageSizeState}
                            placeholder='20'
                            triggerClassName='w-[70px]'
                            values={[
                                {
                                    label: '10',
                                    value: '10',
                                    onClick: (): void => table.setPageSize(10),
                                },
                                {
                                    label: '20',
                                    value: '20',
                                    onClick: (): void => table.setPageSize(20),
                                },
                                {
                                    label: '50',
                                    value: '50',
                                    onClick: (): void => table.setPageSize(50),
                                },
                                {
                                    label: '100',
                                    value: '100',
                                    onClick: (): void => table.setPageSize(100),
                                },
                            ]}
                            isAutoPosition={true}
                            errors={[]}
                        />
                    </div>

                    <div className='flex items-center gap-x-4'>
                        <div className='text-xs font-medium text-muted-foreground'>
                            Page <strong>{pageIndex + 1}</strong> of <strong>{table.getPageCount()}</strong>
                        </div>
                        <div className='flex items-center gap-x-2'>
                            <IconButton
                                onClick={() => table.setPageIndex(0)}
                                disabled={!table.getCanPreviousPage()}
                                icon='chevrons-left'
                                size='sm'
                                variant='outline'
                            />
                            <IconButton
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                                icon='caret-left'
                                size='sm'
                                variant='outline'
                            />
                            <IconButton
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                                icon='caret-right'
                                size='sm'
                                variant='outline'
                            />
                            <IconButton
                                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                disabled={!table.getCanNextPage()}
                                icon='chevrons-right'
                                size='sm'
                                variant='outline'
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);
